import { Box, Typography } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { LabelText, ValueText } from '~/components/Fields/LabeledValue';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import ReviewStatus from '~/components/Structures/ReviewStatus';
import { Modality, sopClassName } from '~/domain/dicom/modality';
import { Image3D } from '~/domain/patient/image3d';
import { useImageReferencesView } from '~/hooks/domain/useImageReferencesView';
import { useImageStreamProgress } from '~/hooks/image/useImageStreamProgress';
import { formatSeriesDate } from '~/utils/date';
import { transform3DImageData } from '~/utils/transforms/imageDataTransforms';
import { formatMmAsCmAtScale } from '~/utils/units';
import IdentitySection from './IdentitySection';
import { useIsInObjectDetails } from './objectDetailsContext';
import PropertySection, {
    CompactRow,
    CountedRowGrid,
    PropertySectionColumns,
} from './PropertySection';
import PropertiesPanel, { PropertiesVariant } from './PropertiesPanel';

interface ImagePropertiesProps {
    image: Image3D;
    hasSharedFrameOfReference?: boolean;
    onRename?: () => void;
    /** Opens the image's DICOM tags; a hover panel's route to them. */
    onViewDicom?: () => void;
    onDelete?: () => void;
    /** The layout of the list; `compact` by default. */
    variant?: PropertiesVariant;
}

export default function ImageProperties({
    image,
    hasSharedFrameOfReference,
    onRename,
    onViewDicom,
    onDelete,
    variant,
}: ImagePropertiesProps) {
    const intl = useIntl();
    const streamProgress = useImageStreamProgress(image);
    const {
        frameOfReferenceUid,
        studyUid,
        sopClassUid,
        modality,
        length,
        instanceCount,
        series,
        rows,
        cols,
        manufacturer,
        manufacturerModelName,
        modalitySpecificAttributes,
        pixelSpacing,
        fieldOfViewMm,
        imageOrientation,
        patientPosition,
        stationName,
        filterType,
        imageType,
        hasInterpolatedSlices,
        interpolatedSliceCount,
    } = transform3DImageData(image);
    // Everything on the patient that depends on this image: the
    // completeness overview a planner reads before starting. Listed in the
    // dialog only, so a hover panel does not walk the patient for a list it
    // has no room for.
    const inDetailsDialog = useIsInObjectDetails();
    const references = useImageReferencesView(
        inDetailsDialog ? series.id : undefined,
        frameOfReferenceUid
    );
    const listReferences = references.length > 0;

    const dash = '—';
    const className = sopClassName(sopClassUid);

    return (
        <PropertiesPanel
            variant={variant}
            onViewDicom={onViewDicom}
            onRename={onRename}
            onDelete={onDelete}
            identity={
                <IdentitySection
                    rows={[
                        {
                            label: intl.formatMessage({ id: 'sopClass' }),
                            value: className ?? sopClassUid,
                            note: className ? sopClassUid : undefined,
                        },
                        {
                            label: intl.formatMessage({
                                id: 'studyInstanceUid',
                            }),
                            id: studyUid,
                        },
                        {
                            label: intl.formatMessage({
                                id: 'seriesInstanceUid',
                            }),
                            id: series.id,
                        },
                        {
                            label: intl.formatMessage({
                                id: 'frameOfReferenceUid',
                            }),
                            id: frameOfReferenceUid,
                        },
                        {
                            label: intl.formatMessage({
                                id: 'seriesNumberLabel',
                            }),
                            value:
                                series.number != null
                                    ? String(series.number)
                                    : undefined,
                        },
                        {
                            label: intl.formatMessage({
                                id: 'properties.instances',
                            }),
                            value: String(instanceCount),
                        },
                    ]}
                />
            }
            trailing={
                listReferences ? (
                    <CountedRowGrid
                        id="image.references"
                        count={references.length}
                        data-testid="image-references"
                    >
                        {references.map((reference) => (
                            <CompactRow
                                key={reference.key}
                                sensitive
                                leading={
                                    <LabelText sx={{ fontSize: 10 }}>
                                        <FormattedMessage id={reference.kind} />
                                    </LabelText>
                                }
                                trailing={
                                    reference.approvalStatus && (
                                        <ReviewStatus
                                            status={reference.approvalStatus}
                                            fontSize={10}
                                            color="text.secondary"
                                        />
                                    )
                                }
                            >
                                {(reference.label || dash) +
                                    (reference.kind === 'structureSet'
                                        ? ` (${reference.roiCount})`
                                        : '')}
                            </CompactRow>
                        ))}
                    </CountedRowGrid>
                ) : undefined
            }
            subtitle={
                hasSharedFrameOfReference ? (
                    <Typography
                        fontSize={11}
                        color="text.disabled"
                        mb={1}
                        mt={-0.5}
                    >
                        <FormattedMessage id="workflow.imageRegistration.sameFOR" />
                    </Typography>
                ) : undefined
            }
            layout="sections"
        >
            <PropertySectionColumns>
                <PropertySection id="properties.geometry">
                    <LabelText>
                        <FormattedMessage id="modality" />
                    </LabelText>
                    <ValueText component="span">{modality || dash}</ValueText>

                    <LabelText>
                        <FormattedMessage id="slices" />
                    </LabelText>
                    <ValueText component="span">{length}</ValueText>

                    {/* Retrieval state, kept above and apart from the
                        interpolation rows below: "not here yet" and
                        "synthesized" are different clinical facts and must not
                        read as one row's two wordings. Coloured `info` /
                        `error`, never the interpolation amber. */}
                    {!streamProgress.isComplete &&
                        (streamProgress.isIncompleteFinal ? (
                            <>
                                <LabelText>
                                    <FormattedMessage id="image.incomplete.label" />
                                </LabelText>
                                <ValueText
                                    component="span"
                                    sx={{ color: 'error.main' }}
                                    data-testid="image-properties-incomplete"
                                >
                                    <FormattedMessage
                                        id="image.incomplete.value"
                                        values={{
                                            unavailable:
                                                streamProgress.unavailableSliceCount,
                                            total: streamProgress.expectedSliceCount,
                                        }}
                                    />
                                </ValueText>
                            </>
                        ) : (
                            <>
                                <LabelText>
                                    <FormattedMessage id="image.loading.label" />
                                </LabelText>
                                <ValueText
                                    component="span"
                                    sx={{ color: 'info.main' }}
                                    data-testid="image-properties-loading"
                                >
                                    <FormattedMessage
                                        id="image.loading.value"
                                        values={{
                                            loaded: streamProgress.loadedSliceCount,
                                            total: streamProgress.expectedSliceCount,
                                        }}
                                    />
                                </ValueText>
                            </>
                        ))}

                    {hasInterpolatedSlices && (
                        <>
                            <LabelText>
                                <FormattedMessage id="image.interpolatedSlices.label" />
                            </LabelText>
                            <ValueText
                                component="span"
                                sx={{ color: 'warning.main' }}
                            >
                                <FormattedMessage
                                    id="image.interpolatedSlices.value"
                                    values={{
                                        count: interpolatedSliceCount,
                                        total: length,
                                    }}
                                />
                            </ValueText>
                        </>
                    )}

                    <LabelText>
                        <FormattedMessage id="slice.sampling.between" /> (cm)
                    </LabelText>
                    <ValueText component="span">
                        {pixelSpacing?.[2] != null
                            ? formatMmAsCmAtScale(pixelSpacing[2], 'voxel')
                            : dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="resolution" /> (cm)
                    </LabelText>
                    <ValueText component="span">
                        {pixelSpacing
                            ? pixelSpacing
                                  .map((x) => formatMmAsCmAtScale(x, 'voxel'))
                                  .join(' x ')
                            : dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="matrix" />
                    </LabelText>
                    <ValueText component="span">
                        {`${cols} x ${rows} x ${length}`}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="fieldOfView" /> (cm)
                    </LabelText>
                    <ValueText component="span">
                        {fieldOfViewMm
                            .map((v) => formatMmAsCmAtScale(v, 'macro'))
                            .join(' x ')}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="imageOrientation" />
                    </LabelText>
                    <ValueText component="span">
                        {imageOrientation ? (
                            <Box display="inline-flex" gap={0.5}>
                                {imageOrientation.map((axis, index) => (
                                    <span key={index}>
                                        [
                                        {axis
                                            .map((coord) =>
                                                Math.abs(coord) == 1 ||
                                                coord == 0
                                                    ? coord
                                                    : coord.toFixed(2)
                                            )
                                            .join(',')}
                                        ]
                                    </span>
                                ))}
                            </Box>
                        ) : (
                            dash
                        )}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="patientPosition" />
                    </LabelText>
                    <ValueText component="span">
                        {patientPosition || dash}
                    </ValueText>
                </PropertySection>

                <PropertySection id="properties.acquisition">
                    <LabelText>
                        <FormattedMessage id="seriesDescription" />
                    </LabelText>
                    <ValueText component="span">
                        <SensitiveInfoTypography>
                            {series.description || dash}
                        </SensitiveInfoTypography>
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="seriesDate" />
                    </LabelText>
                    <ValueText component="span">
                        <SensitiveInfoTypography>
                            {formatSeriesDate(series?.date) || dash}
                        </SensitiveInfoTypography>
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="manufacturer" />
                    </LabelText>
                    <ValueText component="span">
                        {manufacturer || dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="manufacturerModelName" />
                    </LabelText>
                    <ValueText component="span">
                        {manufacturerModelName || dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="stationName" />
                    </LabelText>
                    <ValueText component="span">
                        {stationName || dash}
                    </ValueText>

                    <LabelText>
                        <FormattedMessage id="filterType" />
                    </LabelText>
                    <ValueText component="span">{filterType || dash}</ValueText>

                    <LabelText>
                        <FormattedMessage id="imageType" />
                    </LabelText>
                    <ValueText component="span">
                        {imageType.length > 0 ? imageType.join(', ') : dash}
                    </ValueText>

                    {modality === Modality.CT && (
                        <>
                            <LabelText>
                                <FormattedMessage id="kvp" />
                            </LabelText>
                            <ValueText component="span">
                                {modalitySpecificAttributes?.kvp || dash}
                            </ValueText>

                            <LabelText>
                                <FormattedMessage id="ctdi" />
                            </LabelText>
                            <ValueText component="span">
                                {modalitySpecificAttributes?.ctdi || dash}
                            </ValueText>

                            <LabelText>
                                <FormattedMessage id="convolutionKernel" />
                            </LabelText>
                            <ValueText component="span">
                                {modalitySpecificAttributes?.kernel || dash}
                            </ValueText>

                            <LabelText>
                                <FormattedMessage id="protocolName" />
                            </LabelText>
                            <ValueText component="span">
                                {modalitySpecificAttributes?.protocolName ||
                                    dash}
                            </ValueText>
                        </>
                    )}

                    {modality === Modality.MR && (
                        <>
                            <LabelText>
                                <FormattedMessage id="scanningSequence" />
                            </LabelText>
                            <ValueText component="span">
                                {modalitySpecificAttributes?.scanningSequence ||
                                    dash}
                            </ValueText>

                            <LabelText>
                                <FormattedMessage id="mrAcquisitionType" />
                            </LabelText>
                            <ValueText component="span">
                                {modalitySpecificAttributes?.mraAcquisitionType ||
                                    dash}
                            </ValueText>

                            <LabelText>
                                <FormattedMessage id="sequenceVariant" />
                            </LabelText>
                            <ValueText component="span">
                                {modalitySpecificAttributes?.sequenceVariant ||
                                    dash}
                            </ValueText>
                        </>
                    )}
                </PropertySection>
            </PropertySectionColumns>
        </PropertiesPanel>
    );
}
